import { type ReactNode, useEffect } from 'react';
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web';
import { keycloak, KEYCLOAK_INIT_OPTIONS } from '../auth/keycloak';
import { useAuthStore } from '../stores/auth-store';
import { setAccessToken } from '../api/client';
import { LoadingScreen } from '../components/LoadingScreen';
import { scheduleTokenRefresh } from './scheduleTokenRefresh';

interface KeycloakProviderProps {
  children: ReactNode;
  onReady: () => void;
}

/**
 * Syncs Keycloak auth state into the local store + axios header.
 * Same pattern as TePresta: ReactKeycloakProvider + login-required.
 */
function KeycloakStateSync({ onReady }: { onReady: () => void }) {
  const { keycloak: kc, initialized } = useKeycloak();
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setInitialized = useAuthStore((s) => s.setInitialized);

  useEffect(() => {
    if (!initialized) return;
    setInitialized(true);
    const authenticated = !!kc.authenticated;
    setAuthenticated(authenticated);
    setAccessToken(authenticated && kc.token ? kc.token : undefined);
    onReady();
  }, [initialized, kc, setAuthenticated, setInitialized, onReady]);

  return null;
}

/**
 * Proactively renews the access token every minute so mid-session requests
 * never hit an expired token. Silent on failure — the axios 401 interceptor
 * keeps its fallback flow.
 */
function TokenRefreshScheduler() {
  const { keycloak: kc, initialized } = useKeycloak();
  const authenticated = !!kc.authenticated;

  useEffect(() => {
    if (!initialized || !authenticated) return;
    return scheduleTokenRefresh(kc);
  }, [initialized, authenticated, kc]);

  return null;
}

export function KeycloakProvider({ children, onReady }: KeycloakProviderProps) {
  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={KEYCLOAK_INIT_OPTIONS}
      LoadingComponent={<LoadingScreen />}
      onTokens={(kc) => setAccessToken(kc.token)}
      onEvent={(event) => {
        if (event === 'onAuthLogout' || event === 'onAuthError') {
          setAccessToken(undefined);
        }
      }}
    >
      <KeycloakStateSync onReady={onReady} />
      <TokenRefreshScheduler />
      {children}
    </ReactKeycloakProvider>
  );
}
